import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, Calendar, ChevronDown, Download, BarChart3, Package } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import WebvonkLoader from '../components/WebvonkLoader';
import { spacing, borderRadius, fontSize, fontWeight, shadows } from '../../styles/theme';
import { getKmfcClient } from '../../lib/supabase';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
  Label,
  LabelList
} from 'recharts';

// ─── Styled Components ───────────────────────────────────────────────────────
const Container = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${spacing.xl};
`;

const HeaderLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.xl};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
`;


const Title = styled.h1`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0;
`;

const TopProductsButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  color: ${props => props.theme.colors.text};
  font-size: ${fontSize.sm};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.border};
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
`;

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  cursor: pointer;
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }
`;

const DateBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.medium};
  color: ${props => props.theme.colors.text};
  transition: all 0.2s ease;

  ${props => props.active && `
    background: ${props.theme.colors.primary};
    color: white;
    border-color: ${props.theme.colors.primary};
  `}

  &:hover {
    background: ${props => props.active ? props.theme.colors.primaryDark : props.theme.colors.backgroundHover};
  }
`;

const DatePopover = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: ${spacing.xs};
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.md};
  padding: ${spacing.md};
  box-shadow: ${shadows.lg};
  z-index: 1000;
  min-width: 200px;
`;

const DateLabel = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing.sm};
`;

const DateInputRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

const DateInput = styled.input`
  padding: ${spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.sm};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const DateCloseBtn = styled.button`
  width: 100%;
  padding: ${spacing.sm};
  background-color: ${props => props.theme.colors.lightGray};
  border: none;
  border-radius: ${borderRadius.sm};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.medium};
  
  &:hover {
    background-color: ${props => props.theme.colors.border};
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: ${spacing.xl};
  margin-bottom: ${spacing.xl};
`;

const ChartCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${shadows.md};
  }
`;

const ChartTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${spacing.lg} 0;
`;

const FullWidthChart = styled(ChartCard)`
  grid-column: 1 / -1;
`;

// Helper function for calculating percentage changes
const calculatePercentageChange = (current, previous) => {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};

// Helper function for formatting chart labels
const formatChartLabel = (value, periodMode) => {
  if (periodMode === '12months') {
    // 12 months mode: "Okt 2024", "Nov 2024"
    const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
    const currentYear = new Date().getFullYear();
    return `${value} ${currentYear}`; // value is month name like "Jan"
  } else {
    // 1 month mode: "1 Jan", "2 Jan", "3 Jan"
    const date = new Date(value);
    const day = date.getDate();
    const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    return `${day} ${month}`;
  }
};

// Custom label component for percentage changes
const PercentageLabel = ({ x, y, value, index, data }) => {
  if (index === 0) return null; // Skip first point
  
  const currentValue = data[index].orders;
  const previousValue = data[index - 1].orders;
  const percentage = calculatePercentageChange(currentValue, previousValue);
  
  if (Math.abs(percentage) < 5) return null; // Skip small changes
  
  const color = percentage > 0 ? '#10b981' : percentage < 0 ? '#ef4444' : '#f59e0b';
  const symbol = percentage > 0 ? '+' : '';
  
  return (
    <text 
      x={x} 
      y={y - 10} 
      fill={color} 
      fontSize={10} 
      fontWeight="bold"
      textAnchor="middle"
    >
      {symbol}{percentage.toFixed(0)}%
    </text>
  );
};

const formatDateKey = (d) => d.toISOString().split('T')[0];

const getDateLabel = (monthString) => {
  const [year, month] = monthString.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  const monthNames = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

const getMonthRange = (monthString) => {
  const [year, month] = monthString.split('-');
  const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
  const endDate = new Date(parseInt(year), parseInt(month), 0); // Last day of month
  return {
    start: formatDateKey(startDate),
    end: formatDateKey(endDate)
  };
};

// ─── Main Component ───────────────────────────────────────────────────────
export default function ChartsScreen() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [orders, setOrders] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  });
  const [periodMode, setPeriodMode] = useState('month'); // 'month' or '12months'

  useEffect(() => {
    const loadData = async () => {
      try {
        const kmfcClient = getKmfcClient();
        if (!kmfcClient) return;

        // Load orders (last 12 months, only needed columns)
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        const twelveMonthsAgoStr = twelveMonthsAgo.toISOString().split('T')[0];

        const { data: ordersData, error: ordersError } = await kmfcClient
          .from('orders')
          .select('placed_at, created_at, total_cents, status, is_test')
          .gte('placed_at', twelveMonthsAgoStr)
          .order('placed_at', { ascending: false });

        if (!ordersError && ordersData) {
          setOrders(ordersData);
        }

        // Load visitors (last 12 months, only created_at)
        const { data: visitorsData, error: visitorsError } = await kmfcClient
          .from('visitors')
          .select('created_at')
          .gte('created_at', twelveMonthsAgo.toISOString())
          .order('created_at', { ascending: false });

        if (!visitorsError && visitorsData) {
          setVisitors(visitorsData);
        }

        setLastUpdateTime(new Date());
      } catch (error) {
        console.error('Error loading chart data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const prepareOrdersData = () => {
    if (periodMode === '12months') {
      // 12 months mode - aggregate by month
      const monthlyData = {};
      const today = new Date();
      const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
      
      for (let i = 11; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const monthKey = monthNames[date.getMonth()];
        monthlyData[monthKey] = { date: monthKey, orders: 0, revenue: 0 };
      }
      
      orders.forEach(order => {
        const orderDate = new Date(order.placed_at || order.created_at);
        const monthKey = monthNames[orderDate.getMonth()];
        
        if (monthlyData[monthKey]) {
          monthlyData[monthKey].orders++;
          monthlyData[monthKey].revenue += (order.total_cents || 0) / 100;
        }
      });
      
      // Return in correct chronological order
      return Object.values(monthlyData);
    } else {
      // 1 month mode - daily data
      const { start, end } = getMonthRange(selectedMonth);
      const dailyData = {};
      
      const startDate = new Date(start);
      const endDate = new Date(end);
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateKey = formatDateKey(new Date(currentDate));
        dailyData[dateKey] = { date: dateKey, orders: 0, revenue: 0 };
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      orders.forEach(order => {
        const date = formatDateKey(new Date(order.placed_at || order.created_at));
        if (date >= start && date <= end) {
          if (dailyData[date]) {
            dailyData[date].orders++;
            dailyData[date].revenue += (order.total_cents || 0) / 100;
          }
        }
      });

      return Object.values(dailyData)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    }
  };

  const prepareVisitorsData = () => {
    if (periodMode === '12months') {
      // 12 months mode - aggregate by month
      const monthlyData = {};
      const today = new Date();
      const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
      
      for (let i = 11; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const monthKey = monthNames[date.getMonth()];
        monthlyData[monthKey] = { date: monthKey, visitors: 0, pageViews: 0 };
      }
      
      visitors.forEach(visitor => {
        const visitDate = new Date(visitor.created_at);
        const monthKey = monthNames[visitDate.getMonth()];
        
        if (monthlyData[monthKey]) {
          monthlyData[monthKey].visitors++;
          monthlyData[monthKey].pageViews += 3.5; // Average pages per visit
        }
      });
      
      return Object.values(monthlyData);
    } else {
      // 1 month mode - daily data
      const { start, end } = getMonthRange(selectedMonth);
      const dailyData = {};
      
      const startDate = new Date(start);
      const endDate = new Date(end);
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateKey = formatDateKey(new Date(currentDate));
        dailyData[dateKey] = { date: dateKey, visitors: 0, pageViews: 0 };
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      visitors.forEach(visitor => {
        const date = formatDateKey(new Date(visitor.created_at));
        if (date >= start && date <= end) {
          if (dailyData[date]) {
            dailyData[date].visitors++;
          }
        }
      });

      // Calculate pageViews based on visitors
      Object.keys(dailyData).forEach(date => {
        dailyData[date].pageViews = dailyData[date].visitors * 3.5;
      });

      return Object.values(dailyData)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    }
  };

  const prepareDeviceData = () => {
    const { start, end } = getMonthRange(selectedMonth);
    const deviceCounts = { desktop: 0, mobile: 0, tablet: 0 };
    
    visitors.forEach(visitor => {
      const date = formatDateKey(new Date(visitor.created_at));
      if (date >= start && date <= end) {
        const userAgent = visitor.user_agent?.toLowerCase() || '';
        if (userAgent.includes('mobile') || userAgent.includes('android') || userAgent.includes('iphone')) {
          deviceCounts.mobile++;
        } else if (userAgent.includes('tablet') || userAgent.includes('ipad')) {
          deviceCounts.tablet++;
        } else {
          deviceCounts.desktop++;
        }
      }
    });

    return [
      { name: 'Desktop', value: deviceCounts.desktop, color: '#3b82f6' },
      { name: 'Mobiel', value: deviceCounts.mobile, color: '#10b981' },
      { name: 'Tablet', value: deviceCounts.tablet, color: '#f59e0b' }
    ];
  };

  const prepareConversionRateData = () => {
    if (periodMode === '12months') {
      // 12 months mode - aggregate by month
      const monthlyData = {};
      const today = new Date();
      const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
      
      for (let i = 11; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const monthKey = monthNames[date.getMonth()];
        monthlyData[monthKey] = { date: monthKey, orders: 0, uniqueVisitors: 0, conversionRate: 0 };
      }
      
      // Count orders per month
      orders.forEach(order => {
        const orderDate = new Date(order.placed_at || order.created_at);
        const monthKey = monthNames[orderDate.getMonth()];
        
        if (monthlyData[monthKey]) {
          monthlyData[monthKey].orders++;
        }
      });
      
      // Count unique visitors per month (using user_id like StatsScreen)
      const monthlyVisitorSets = {};
      visitors.forEach(visitor => {
        const visitDate = new Date(visitor.created_at);
        const monthKey = monthNames[visitDate.getMonth()];
        
        if (monthlyData[monthKey]) {
          if (!monthlyVisitorSets[monthKey]) {
            monthlyVisitorSets[monthKey] = new Set();
          }
          monthlyVisitorSets[monthKey].add(visitor.user_id);
        }
      });
      
      // Set unique visitor counts
      Object.keys(monthlyVisitorSets).forEach(month => {
        if (monthlyData[month]) {
          monthlyData[month].uniqueVisitors = monthlyVisitorSets[month].size;
        }
      });
      
      // Calculate conversion rate
      Object.keys(monthlyData).forEach(month => {
        const data = monthlyData[month];
        data.conversionRate = data.uniqueVisitors > 0 ? (data.orders / data.uniqueVisitors) * 100 : 0;
      });
      
      return Object.values(monthlyData);
    } else {
      // 1 month mode - daily data
      const { start, end } = getMonthRange(selectedMonth);
      const dailyData = {};
      const dailyVisitorSets = {};
      
      const startDate = new Date(start);
      const endDate = new Date(end);
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateKey = formatDateKey(new Date(currentDate));
        dailyData[dateKey] = { date: dateKey, orders: 0, uniqueVisitors: 0, conversionRate: 0 };
        dailyVisitorSets[dateKey] = new Set();
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Count orders per day
      orders.forEach(order => {
        const date = formatDateKey(new Date(order.placed_at || order.created_at));
        if (date >= start && date <= end) {
          if (dailyData[date]) {
            dailyData[date].orders++;
          }
        }
      });
      
      // Count unique visitors per day (using user_id like StatsScreen)
      visitors.forEach(visitor => {
        const date = formatDateKey(new Date(visitor.created_at));
        if (date >= start && date <= end) {
          if (dailyVisitorSets[date]) {
            dailyVisitorSets[date].add(visitor.user_id);
          }
        }
      });
      
      // Set unique visitor counts
      Object.keys(dailyVisitorSets).forEach(date => {
        if (dailyData[date]) {
          dailyData[date].uniqueVisitors = dailyVisitorSets[date].size;
        }
      });
      
      // Calculate conversion rate
      Object.keys(dailyData).forEach(date => {
        const data = dailyData[date];
        data.conversionRate = data.uniqueVisitors > 0 ? (data.orders / data.uniqueVisitors) * 100 : 0;
      });

      return Object.values(dailyData)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    }
  };

  const prepareOnTimeDeliveryData = () => {
    if (periodMode === '12months') {
      // 12 months mode - aggregate by month
      const monthlyData = {};
      const today = new Date();
      const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
      
      for (let i = 11; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const monthKey = monthNames[date.getMonth()];
        monthlyData[monthKey] = { date: monthKey, totalOrders: 0, onTimeOrders: 0, onTimePercentage: 0 };
      }
      
      // Count orders and on-time deliveries per month
      orders.forEach(order => {
        const orderDate = new Date(order.placed_at || order.created_at);
        const monthKey = monthNames[orderDate.getMonth()];
        
        if (monthlyData[monthKey]) {
          monthlyData[monthKey].totalOrders++;
          
          // Check if delivered on time (assuming delivered_at exists and we have a delivery time target)
          // For now, let's assume on-time if delivered within 2 days (48 hours)
          if (order.delivered_at) {
            const placedTime = new Date(order.placed_at || order.created_at).getTime();
            const deliveredTime = new Date(order.delivered_at).getTime();
            const hoursDiff = (deliveredTime - placedTime) / (1000 * 60 * 60);
            
            if (hoursDiff <= 48) { // 48 hours = 2 days
              monthlyData[monthKey].onTimeOrders++;
            }
          }
        }
      });
      
      // Calculate on-time percentage
      Object.keys(monthlyData).forEach(month => {
        const data = monthlyData[month];
        data.onTimePercentage = data.totalOrders > 0 ? (data.onTimeOrders / data.totalOrders) * 100 : 0;
      });
      
      return Object.values(monthlyData);
    } else {
      // 1 month mode - daily data
      const { start, end } = getMonthRange(selectedMonth);
      const dailyData = {};
      
      const startDate = new Date(start);
      const endDate = new Date(end);
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateKey = formatDateKey(new Date(currentDate));
        dailyData[dateKey] = { date: dateKey, totalOrders: 0, onTimeOrders: 0, onTimePercentage: 0 };
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Count orders and on-time deliveries per day
      orders.forEach(order => {
        const date = formatDateKey(new Date(order.placed_at || order.created_at));
        if (date >= start && date <= end) {
          if (dailyData[date]) {
            dailyData[date].totalOrders++;
            
            // Check if delivered on time
            if (order.delivered_at) {
              const placedTime = new Date(order.placed_at || order.created_at).getTime();
              const deliveredTime = new Date(order.delivered_at).getTime();
              const hoursDiff = (deliveredTime - placedTime) / (1000 * 60 * 60);
              
              if (hoursDiff <= 48) { // 48 hours = 2 days
                dailyData[date].onTimeOrders++;
              }
            }
          }
        }
      });
      
      // Calculate on-time percentage
      Object.keys(dailyData).forEach(date => {
        const data = dailyData[date];
        data.onTimePercentage = data.totalOrders > 0 ? (data.onTimeOrders / data.totalOrders) * 100 : 0;
      });

      return Object.values(dailyData)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    }
  };

  const prepareTipsData = () => {
    if (periodMode === '12months') {
      // 12 months mode - aggregate by month
      const monthlyData = {};
      const today = new Date();
      const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
      
      for (let i = 11; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const monthKey = monthNames[date.getMonth()];
        monthlyData[monthKey] = { date: monthKey, tips: 0, orderCount: 0, averageTip: 0 };
      }
      
      orders.forEach(order => {
        const orderDate = new Date(order.placed_at || order.created_at);
        const monthKey = monthNames[orderDate.getMonth()];
        
        if (monthlyData[monthKey]) {
          monthlyData[monthKey].orderCount++;
          // Using tip_cents from StatsScreen
          monthlyData[monthKey].tips += (order.tip_cents || 0) / 100;
        }
      });
      
      // Calculate average tip
      Object.keys(monthlyData).forEach(month => {
        const data = monthlyData[month];
        data.averageTip = data.orderCount > 0 ? data.tips / data.orderCount : 0;
      });
      
      return Object.values(monthlyData);
    } else {
      // 1 month mode - daily data
      const { start, end } = getMonthRange(selectedMonth);
      const dailyData = {};
      
      const startDate = new Date(start);
      const endDate = new Date(end);
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateKey = formatDateKey(new Date(currentDate));
        dailyData[dateKey] = { date: dateKey, tips: 0, orderCount: 0, averageTip: 0 };
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      orders.forEach(order => {
        const date = formatDateKey(new Date(order.placed_at || order.created_at));
        if (date >= start && date <= end) {
          if (dailyData[date]) {
            dailyData[date].orderCount++;
            dailyData[date].tips += (order.tip_cents || 0) / 100;
          }
        }
      });
      
      // Calculate average tip
      Object.keys(dailyData).forEach(date => {
        const data = dailyData[date];
        data.averageTip = data.orderCount > 0 ? data.tips / data.orderCount : 0;
      });

      return Object.values(dailyData)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    }
  };

  const prepareStripeCostsData = () => {
    if (periodMode === '12months') {
      // 12 months mode - aggregate by month
      const monthlyData = {};
      const today = new Date();
      const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
      
      for (let i = 11; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const monthKey = monthNames[date.getMonth()];
        monthlyData[monthKey] = { date: monthKey, stripeCosts: 0, revenue: 0, costPercentage: 0 };
      }
      
      orders.forEach(order => {
        const orderDate = new Date(order.placed_at || order.created_at);
        const monthKey = monthNames[orderDate.getMonth()];
        
        if (monthlyData[monthKey]) {
          const revenue = (order.total_cents || 0) / 100;
          monthlyData[monthKey].revenue += revenue;
          // Stripe fees: 1.5% + €0.25 per transaction (typical Dutch rates)
          const stripeFee = (revenue * 0.015) + 0.25;
          monthlyData[monthKey].stripeCosts += stripeFee;
        }
      });
      
      // Calculate cost percentage
      Object.keys(monthlyData).forEach(month => {
        const data = monthlyData[month];
        data.costPercentage = data.revenue > 0 ? (data.stripeCosts / data.revenue) * 100 : 0;
      });
      
      return Object.values(monthlyData);
    } else {
      // 1 month mode - daily data
      const { start, end } = getMonthRange(selectedMonth);
      const dailyData = {};
      
      const startDate = new Date(start);
      const endDate = new Date(end);
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateKey = formatDateKey(new Date(currentDate));
        dailyData[dateKey] = { date: dateKey, stripeCosts: 0, revenue: 0, costPercentage: 0 };
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      orders.forEach(order => {
        const date = formatDateKey(new Date(order.placed_at || order.created_at));
        if (date >= start && date <= end) {
          if (dailyData[date]) {
            const revenue = (order.total_cents || 0) / 100;
            dailyData[date].revenue += revenue;
            const stripeFee = (revenue * 0.015) + 0.25;
            dailyData[date].stripeCosts += stripeFee;
          }
        }
      });
      
      // Calculate cost percentage
      Object.keys(dailyData).forEach(date => {
        const data = dailyData[date];
        data.costPercentage = data.revenue > 0 ? (data.stripeCosts / data.revenue) * 100 : 0;
      });

      return Object.values(dailyData)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    }
  };

  const prepareAverageOrderValueData = () => {
    if (periodMode === '12months') {
      // 12 months mode - aggregate by month
      const monthlyData = {};
      const today = new Date();
      const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
      
      for (let i = 11; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const monthKey = monthNames[date.getMonth()];
        monthlyData[monthKey] = { date: monthKey, totalRevenue: 0, orderCount: 0, averageOrderValue: 0 };
      }
      
      orders.forEach(order => {
        const orderDate = new Date(order.placed_at || order.created_at);
        const monthKey = monthNames[orderDate.getMonth()];
        
        if (monthlyData[monthKey]) {
          monthlyData[monthKey].orderCount++;
          monthlyData[monthKey].totalRevenue += (order.total_cents || 0) / 100;
        }
      });
      
      // Calculate average order value
      Object.keys(monthlyData).forEach(month => {
        const data = monthlyData[month];
        data.averageOrderValue = data.orderCount > 0 ? data.totalRevenue / data.orderCount : 0;
      });
      
      return Object.values(monthlyData);
    } else {
      // 1 month mode - daily data
      const { start, end } = getMonthRange(selectedMonth);
      const dailyData = {};
      
      const startDate = new Date(start);
      const endDate = new Date(end);
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateKey = formatDateKey(new Date(currentDate));
        dailyData[dateKey] = { date: dateKey, totalRevenue: 0, orderCount: 0, averageOrderValue: 0 };
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      orders.forEach(order => {
        const date = formatDateKey(new Date(order.placed_at || order.created_at));
        if (date >= start && date <= end) {
          if (dailyData[date]) {
            dailyData[date].orderCount++;
            dailyData[date].totalRevenue += (order.total_cents || 0) / 100;
          }
        }
      });
      
      // Calculate average order value
      Object.keys(dailyData).forEach(date => {
        const data = dailyData[date];
        data.averageOrderValue = data.orderCount > 0 ? data.totalRevenue / data.orderCount : 0;
      });

      return Object.values(dailyData)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    }
  };

  const prepareRevenueData = () => {
    if (periodMode === '12months') {
      // 12 months mode - use existing logic
      const monthlyData = {};
      const today = new Date();
      const monthNames = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
      
      for (let i = 11; i >= 0; i--) {
        const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const monthKey = monthNames[date.getMonth()];
        
        const year = date.getFullYear();
        const month = date.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const daysPassed = month === today.getMonth() && year === today.getFullYear() 
          ? today.getDate() 
          : daysInMonth;
        
        monthlyData[monthKey] = { 
          month: monthKey, 
          revenue: 0,
          daysInMonth: daysInMonth,
          daysPassed: daysPassed
        };
      }
      
      orders.forEach(order => {
        const orderDate = new Date(order.placed_at || order.created_at);
        const monthKey = monthNames[orderDate.getMonth()];
        
        if (monthlyData[monthKey]) {
          monthlyData[monthKey].revenue += (order.total_cents || 0) / 100;
        }
      });

      return Object.values(monthlyData);
    } else {
      // 1 month mode - daily data for selected month
      const { start, end } = getMonthRange(selectedMonth);
      const dailyData = {};
      
      const startDate = new Date(start);
      const endDate = new Date(end);
      const currentDate = new Date(startDate);
      
      while (currentDate <= endDate) {
        const dateKey = formatDateKey(new Date(currentDate));
        dailyData[dateKey] = { 
          month: dateKey, 
          revenue: 0,
          daysInMonth: endDate.getDate(),
          daysPassed: currentDate.getDate()
        };
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      orders.forEach(order => {
        const date = formatDateKey(new Date(order.placed_at || order.created_at));
        if (date >= start && date <= end) {
          if (dailyData[date]) {
            dailyData[date].revenue += (order.total_cents || 0) / 100;
          }
        }
      });

      return Object.values(dailyData);
    }
  };

  const ordersData = prepareOrdersData();
  const visitorsData = prepareVisitorsData();
  const deviceData = prepareDeviceData();
  const revenueData = prepareRevenueData();
  const conversionRateData = prepareConversionRateData();
  const tipsData = prepareTipsData();
  const stripeCostsData = prepareStripeCostsData();
  const averageOrderValueData = prepareAverageOrderValueData();
  const onTimeDeliveryData = prepareOnTimeDeliveryData();

  const exportData = () => {
    const csvContent = [
      ['Datum', 'Bestellingen', 'Bezoekers', 'Omzet'],
      ...ordersData.map(item => [
        item.date,
        item.orders,
        visitorsData.find(v => v.date === item.date)?.visitors || 0,
        item.revenue.toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `charts-data-${formatDateKey(new Date())}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <WebvonkLoader fullscreen text="Grafieken worden geladen..." />;
  }

  return (
    <Container>
      <Content>
        <HeaderLayout>
          <HeaderLeft>
            <Title>Grafieken</Title>
          </HeaderLeft>
          <HeaderRight>
            <div style={{ position: 'relative' }}>
              <DateBadge
                active={isDatePickerOpen}
                onClick={() => setIsDatePickerOpen(p => !p)}
              >
                <Calendar size={10} />
                <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11 }}>
                  {periodMode === '12months' ? '12 maanden' : getDateLabel(selectedMonth)}
                </span>
                <ChevronDown size={8} />
              </DateBadge>
              {isDatePickerOpen && (
                <DatePopover>
                  <DateLabel>Selecteer periode</DateLabel>
                  <DateInputRow>
                    <DateInput 
                      type="month" 
                      value={selectedMonth} 
                      max={formatDateKey(new Date()).slice(0, 7)}
                      onChange={e => setSelectedMonth(e.target.value)} 
                      disabled={periodMode === '12months'}
                    />
                  </DateInputRow>
                  <DateInputRow>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input 
                        type="radio" 
                        name="period" 
                        value="month" 
                        checked={periodMode === 'month'}
                        onChange={() => setPeriodMode('month')}
                      />
                      <span style={{ fontSize: '14px', color: theme.colors.text }}>1 maand</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input 
                        type="radio" 
                        name="period" 
                        value="12months" 
                        checked={periodMode === '12months'}
                        onChange={() => setPeriodMode('12months')}
                      />
                      <span style={{ fontSize: '14px', color: theme.colors.text }}>12 maanden</span>
                    </label>
                  </DateInputRow>
                  <DateCloseBtn onClick={() => setIsDatePickerOpen(false)}>
                    Toepassen
                  </DateCloseBtn>
                </DatePopover>
              )}
            </div>
            <ExportButton onClick={exportData}>
              <Download size={16} />
              Exporteer
            </ExportButton>
          </HeaderRight>
        </HeaderLayout>

        <ChartsGrid>
          {/* Orders Chart */}
          <FullWidthChart>
            <ChartTitle>Bestellingen</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={ordersData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                <XAxis 
                  dataKey="date" 
                  stroke={theme.colors.muted}
                  tick={{ fill: theme.colors.muted, fontSize: 12 }}
                  tickFormatter={(value) => formatChartLabel(value, periodMode)}
                />
                <YAxis stroke={theme.colors.muted} tick={{ fill: theme.colors.muted, fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: theme.colors.surface, 
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: borderRadius.md
                  }}
                  labelStyle={{ color: theme.colors.text }}
                  itemStyle={{ color: theme.colors.text }}
                  formatter={(value, name) => [value, name === 'orders' ? 'Bestellingen' : 'Omzet €']}
                  labelFormatter={(value) => {
                    if (periodMode === '12months') {
                      return value; // Already formatted as "Jan 2026"
                    } else {
                      const date = new Date(value);
                      return date.toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                    }
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                  formatter={(value) => {
                    if (value === 'orders') return 'Bestellingen';
                    if (value === 'revenue') return 'Omzet (€)';
                    return value;
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="orders" 
                  stroke={theme.colors.primary} 
                  strokeWidth={2}
                  dot={{ fill: theme.colors.primary, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="orders"
                />
              </LineChart>
            </ResponsiveContainer>
          </FullWidthChart>

          {/* Visitors Chart */}
          <FullWidthChart>
            <ChartTitle>Bezoekers</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={visitorsData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                <XAxis 
                  dataKey="date" 
                  stroke={theme.colors.muted}
                  tick={{ fill: theme.colors.muted, fontSize: 12 }}
                  tickFormatter={(value) => formatChartLabel(value, periodMode)}
                />
                <YAxis stroke={theme.colors.muted} tick={{ fill: theme.colors.muted, fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: theme.colors.surface, 
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: borderRadius.md
                  }}
                  labelStyle={{ color: theme.colors.text }}
                  itemStyle={{ color: theme.colors.text }}
                  labelFormatter={(value) => {
                    if (periodMode === '12months') {
                      return value; // Already formatted as "Jan 2026"
                    } else {
                      const date = new Date(value);
                      return date.toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                    }
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                  formatter={(value) => {
                    if (value === 'visitors') return 'Unieke bezoekers';
                    if (value === 'pageViews') return 'Paginaweergaves';
                    return value;
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="visitors" 
                  stroke={theme.colors.warning} 
                  strokeWidth={2}
                  dot={{ fill: theme.colors.warning, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="visitors"
                />
                <Line 
                  type="monotone" 
                  dataKey="pageViews" 
                  stroke={theme.colors.success} 
                  strokeWidth={2}
                  dot={{ fill: theme.colors.success, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="pageViews"
                />
              </LineChart>
            </ResponsiveContainer>
          </FullWidthChart>

          {/* Revenue Chart - Full Width */}
          <FullWidthChart>
            <ChartTitle>Omzet</ChartTitle>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                <XAxis 
                  dataKey="month" 
                  stroke={theme.colors.muted}
                  tick={{ fill: theme.colors.muted, fontSize: 12 }}
                  tickFormatter={(value) => formatChartLabel(value, periodMode)}
                />
                <YAxis stroke={theme.colors.muted} tick={{ fill: theme.colors.muted, fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: theme.colors.surface, 
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: borderRadius.md
                  }}
                  labelStyle={{ color: theme.colors.text }}
                  itemStyle={{ color: theme.colors.text }}
                  formatter={(value, name, props) => {
                    const data = props.payload;
                    if (periodMode === '12months') {
                      const daysInfo = data.daysPassed === data.daysInMonth 
                        ? `${data.daysInMonth} dagen` 
                        : `${data.daysPassed}/${data.daysInMonth} dagen`;
                      return [
                        `€${value.toFixed(2)}`,
                        daysInfo
                      ];
                    } else {
                      return [`€${value.toFixed(2)}`, ''];
                    }
                  }}
                  labelFormatter={(label) => periodMode === '12months' ? `Periode: ${label}` : label}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                  formatter={() => 'Omzet (€)'}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke={theme.colors.primary} 
                  strokeWidth={2}
                  dot={{ fill: theme.colors.primary, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="revenue"
                />
              </LineChart>
            </ResponsiveContainer>
          </FullWidthChart>

          {/* Conversion Rate Chart - Full Width */}
          <FullWidthChart>
            <ChartTitle>Conversieratio</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={conversionRateData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                <XAxis 
                  dataKey="date" 
                  stroke={theme.colors.muted}
                  tick={{ fill: theme.colors.muted, fontSize: 12 }}
                  tickFormatter={(value) => formatChartLabel(value, periodMode)}
                />
                <YAxis 
                  stroke={theme.colors.muted} 
                  tick={{ fill: theme.colors.muted, fontSize: 12 }}
                  domain={[0, 'dataMax']}
                  tickFormatter={(value) => `${value.toFixed(1)}%`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: theme.colors.surface, 
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: borderRadius.md
                  }}
                  labelStyle={{ color: theme.colors.text }}
                  itemStyle={{ color: theme.colors.text }}
                  formatter={(value, name, props) => {
                    const data = props.payload;
                    return [
                      `${value.toFixed(2)}%`,
                      `${data.orders} van ${data.uniqueVisitors} unieke bezoekers`
                    ];
                  }}
                  labelFormatter={(value) => {
                    if (periodMode === '12months') {
                      return value; // Already formatted as "Jan 2026"
                    } else {
                      const date = new Date(value);
                      return date.toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                    }
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                  formatter={() => 'Conversieratio (%)'}
                />
                <Line 
                  type="monotone" 
                  dataKey="conversionRate" 
                  stroke={theme.colors.info} 
                  strokeWidth={2}
                  dot={{ fill: theme.colors.info, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="conversionRate"
                />
              </LineChart>
            </ResponsiveContainer>
          </FullWidthChart>

          {/* Tips Chart - Full Width */}
          <FullWidthChart>
            <ChartTitle>Fooien</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={tipsData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                <XAxis 
                  dataKey="date" 
                  stroke={theme.colors.muted}
                  tick={{ fill: theme.colors.muted, fontSize: 12 }}
                  tickFormatter={(value) => formatChartLabel(value, periodMode)}
                />
                <YAxis stroke={theme.colors.muted} tick={{ fill: theme.colors.muted, fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: theme.colors.surface, 
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: borderRadius.md
                  }}
                  labelStyle={{ color: theme.colors.text }}
                  itemStyle={{ color: theme.colors.text }}
                  formatter={(value, name, props) => {
                    const data = props.payload;
                    return [
                      `Gem per bestelling: €${data.averageTip.toFixed(2)}`,
                      `Totaal: €${data.tips.toFixed(2)}`
                    ];
                  }}
                  labelFormatter={(value) => {
                    if (periodMode === '12months') {
                      return value;
                    } else {
                      const date = new Date(value);
                      return date.toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                    }
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                  formatter={() => 'Fooien (€)'}
                />
                <Line 
                  type="monotone" 
                  dataKey="tips" 
                  stroke={theme.colors.success} 
                  strokeWidth={2}
                  dot={{ fill: theme.colors.success, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="tips"
                />
              </LineChart>
            </ResponsiveContainer>
          </FullWidthChart>

          {/* Average Order Value Chart - Full Width */}
          <FullWidthChart>
            <ChartTitle>Gemiddelde Bestelwaarde</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={averageOrderValueData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                <XAxis 
                  dataKey="date" 
                  stroke={theme.colors.muted}
                  tick={{ fill: theme.colors.muted, fontSize: 12 }}
                  tickFormatter={(value) => formatChartLabel(value, periodMode)}
                />
                <YAxis stroke={theme.colors.muted} tick={{ fill: theme.colors.muted, fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: theme.colors.surface, 
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: borderRadius.md
                  }}
                  labelStyle={{ color: theme.colors.text }}
                  itemStyle={{ color: theme.colors.text }}
                  formatter={(value, name, props) => {
                    const data = props.payload;
                    return [
                      `€${value.toFixed(2)}`,
                      `${data.orderCount} bestellingen`
                    ];
                  }}
                  labelFormatter={(value) => {
                    if (periodMode === '12months') {
                      return value;
                    } else {
                      const date = new Date(value);
                      return date.toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                    }
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                  formatter={() => 'Gemiddelde Bestelwaarde (€)'}
                />
                <Line 
                  type="monotone" 
                  dataKey="averageOrderValue" 
                  stroke={theme.colors.warning} 
                  strokeWidth={2}
                  dot={{ fill: theme.colors.warning, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="averageOrderValue"
                />
              </LineChart>
            </ResponsiveContainer>
          </FullWidthChart>

          {/* On-Time Delivery Chart - Full Width */}
          <FullWidthChart>
            <ChartTitle>Op Tijd Bezorgd</ChartTitle>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={onTimeDeliveryData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                <XAxis 
                  dataKey="date" 
                  stroke={theme.colors.muted}
                  tick={{ fill: theme.colors.muted, fontSize: 12 }}
                  tickFormatter={(value) => formatChartLabel(value, periodMode)}
                />
                <YAxis 
                  stroke={theme.colors.muted} 
                  tick={{ fill: theme.colors.muted, fontSize: 12 }}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: theme.colors.surface, 
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: borderRadius.md
                  }}
                  labelStyle={{ color: theme.colors.text }}
                  itemStyle={{ color: theme.colors.text }}
                  formatter={(value, name, props) => {
                    const data = props.payload;
                    return [
                      `${value.toFixed(1)}%`,
                      `${data.onTimeOrders} van ${data.totalOrders} bestellingen`
                    ];
                  }}
                  labelFormatter={(value) => {
                    if (periodMode === '12months') {
                      return value;
                    } else {
                      const date = new Date(value);
                      return date.toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
                    }
                  }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="line"
                  formatter={() => 'Op Tijd Bezorgd (%)'}
                />
                <Line 
                  type="monotone" 
                  dataKey="onTimePercentage" 
                  stroke={theme.colors.primary} 
                  strokeWidth={2}
                  dot={{ fill: theme.colors.primary, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="onTimePercentage"
                />
              </LineChart>
            </ResponsiveContainer>
          </FullWidthChart>
        </ChartsGrid>
      </Content>
    </Container>
  );
}
